/**
 * The vocabulary of the portal. Everything else imports these; nothing
 * re-declares them. Adding a value here is a data-model change and needs a
 * migration plus a policy-matrix run - see docs/workflows/add-a-resource.md.
 */

/** Three roles, enforced server-side. There is no fourth. */
export const ROLES = ['owner', 'contractor', 'client'] as const;
export type Role = (typeof ROLES)[number];

/** Where a piece of work sits. The board has exactly these four columns. */
export const LANES = ['backlog', 'next_up', 'in_progress', 'completed'] as const;
export type Lane = (typeof LANES)[number];

/** The seven SDLC stages. Valid only while lane = 'in_progress'. */
export const ACTIVE_STAGES = [
  'spec',
  'plan',
  'build',
  'test',
  'review',
  'simplify',
  'ship',
] as const;
export type ActiveStage = (typeof ACTIVE_STAGES)[number];

/** The two done stages. Valid only while lane = 'completed'. */
export const DONE_STAGES = ['pilot', 'production'] as const;
export type DoneStage = (typeof DONE_STAGES)[number];

export const STAGES = [...ACTIVE_STAGES, ...DONE_STAGES] as const;
export type Stage = (typeof STAGES)[number];

/**
 * The lane/stage invariant, in one place.
 *
 * backlog and next_up carry no stage. in_progress carries one of the seven
 * SDLC stages. completed carries pilot or production. This is mirrored by a
 * CHECK constraint in the database - the two must never drift.
 */
export function stagesForLane(lane: Lane): readonly Stage[] {
  switch (lane) {
    case 'backlog':
    case 'next_up':
      return [];
    case 'in_progress':
      return ACTIVE_STAGES;
    case 'completed':
      return DONE_STAGES;
  }
}

export function isStageValidForLane(lane: Lane, stage: Stage | null): boolean {
  const allowed = stagesForLane(lane);
  if (allowed.length === 0) return stage === null;
  return stage !== null && (allowed as readonly string[]).includes(stage);
}

/**
 * The stage a task lands on when it is dragged into a lane, so a drop never
 * produces an invalid row. Dropping into a lane with no stage clears it.
 */
export function defaultStageForLane(lane: Lane): Stage | null {
  const allowed = stagesForLane(lane);
  return allowed.length === 0 ? null : (allowed[0] as Stage);
}

/**
 * Story points, Cody's scale: half-steps from 0.5 to 5.0.
 * 1 point is one day. 5 points is a full week, and a BotGuide sprint is a week.
 */
export const STORY_POINTS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5] as const;
export type StoryPoints = (typeof STORY_POINTS)[number];

export function isStoryPoints(n: number): n is StoryPoints {
  return (STORY_POINTS as readonly number[]).includes(n);
}

/** Hours a contractor logs start pending and only an owner moves them. */
export const TIME_ENTRY_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type TimeEntryStatus = (typeof TIME_ENTRY_STATUSES)[number];

/** Default weekly capacity, in story points. One working week. */
export const DEFAULT_WEEKLY_CAPACITY = 5;

export const LANE_LABELS: Record<Lane, string> = {
  backlog: 'Backlog',
  next_up: 'Next Up',
  in_progress: 'In Progress',
  completed: 'Completed',
};

export const STAGE_LABELS: Record<Stage, string> = {
  spec: 'Spec',
  plan: 'Plan',
  build: 'Build',
  test: 'Test',
  review: 'Review',
  simplify: 'Simplify',
  ship: 'Ship',
  // Display only - the stored enum value is still `pilot`, so this is a
  // rename with no migration and no data change behind it.
  pilot: 'Pilot/Staged',
  production: 'Production',
};
